/** Minimal DOM helpers. No framework: the UI is small and this keeps the
 *  reference implementation readable as protocol documentation. */

/** Escape untrusted text. Sender names, subjects and bodies all come from the
 *  wire and must never be interpolated into HTML raw. */
export function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/** Event delegation keyed on data-action, so re-rendering never leaks listeners. */
export function onAction(root, handlers) {
  root.addEventListener('click', (event) => {
    const target = event.target.closest('[data-action]');
    if (!target || !root.contains(target)) return;
    const handler = handlers[target.dataset.action];
    if (!handler) return;
    event.preventDefault();
    handler(target.dataset, target);
  });
}

export function shortNpub(npub) {
  return npub ? `${npub.slice(0, 14)}…${npub.slice(-6)}` : '';
}

export function timeAgo(unixSeconds, locale = 'cs') {
  const seconds = Math.floor(Date.now() / 1000) - unixSeconds;
  const units = [[31536000, 'year'], [2592000, 'month'], [604800, 'week'],
    [86400, 'day'], [3600, 'hour'], [60, 'minute']];
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [size, unit] of units) {
    if (seconds >= size) return rtf.format(-Math.floor(seconds / size), unit);
  }
  return rtf.format(-seconds, 'second');
}

export function formatDate(unixSeconds, locale = 'cs') {
  return new Date(unixSeconds * 1000).toLocaleString(locale, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Non-blocking status line; replaces alert() so flows stay testable. */
export function toast(message, kind = 'info') {
  const host = $('#toast');
  if (!host) return;
  host.className = `toast toast--${kind} toast--visible`;
  host.textContent = message;
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => { host.className = 'toast'; }, 4200);
}

/** Wrap an async action with a busy state on the triggering button. */
export async function withBusy(el, fn) {
  const previous = el?.textContent;
  if (el) { el.disabled = true; el.dataset.busy = '1'; }
  try {
    return await fn();
  } finally {
    if (el) { el.disabled = false; delete el.dataset.busy; if (previous) el.textContent = previous; }
  }
}
